import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { 
  Plus, 
  Search, 
  Target, 
  TrendingUp,
  TrendingDown,
  Users,
  Briefcase,
  Edit,
  Trash2
} from 'lucide-react';
import { api } from '../services/api';

const Skills = () => {
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [skills, setSkills] = useState([]);

  // Load skills from server Neo4j database & localStorage
  useEffect(() => {
    let isMounted = true;
    const loadSkills = () => {
      api.getSkills().then(data => {
        if (isMounted && Array.isArray(data)) {
          setSkills(data);
        }
      }).catch(() => {});
    };

    loadSkills();

    // Listen for storage changes across windows
    const handleStorageChange = () => loadSkills();
    window.addEventListener('storage', handleStorageChange);

    return () => {
      isMounted = false;
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const allPeople = JSON.parse(localStorage.getItem('skillmesh_people') || '[]');
  const allProjects = JSON.parse(localStorage.getItem('skillmesh_projects') || '[]');

  const filteredSkills = skills.map(skill => {
    const sNameLower = (skill.name || '').toLowerCase();
    const countPeople = allPeople.filter(p => 
      (p.skills || []).some(sk => (typeof sk === 'string' ? sk : sk?.name || '').toLowerCase() === sNameLower)
    ).length;
    const countDemand = allProjects.filter(pr => 
      (pr.requiredSkills || []).some(sk => (typeof sk === 'string' ? sk : sk?.name || '').toLowerCase() === sNameLower)
    ).length;
    return {
      ...skill,
      peopleCount: countPeople,
      projectDemand: countDemand,
      gap: countDemand - countPeople
    };
  }).filter(skill => {
    const matchesSearch = (skill.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (skill.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterCategory === 'all' || skill.category === filterCategory;
    return matchesSearch && matchesFilter;
  });

  const getGapColor = (gap) => {
    if (gap > 0) return 'text-red-400';
    if (gap < 0) return 'text-green-400';
    return 'text-yellow-400';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <TrendingUp className="h-4 w-4 text-green-400" />;
      case 'down': return <TrendingDown className="h-4 w-4 text-red-400" />;
      default: return <div className="h-4 w-4" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Frontend': return 'bg-blue-500/20 text-blue-400';
      case 'Backend': return 'bg-green-500/20 text-green-400';
      case 'Design': return 'bg-purple-500/20 text-purple-400';
      case 'Database': return 'bg-orange-500/20 text-orange-400';
      case 'AI/ML': return 'bg-pink-500/20 text-pink-400';
      case 'Infrastructure': return 'bg-indigo-500/20 text-indigo-400';
      default: return isDarkMode ? 'bg-dark-700 text-dark-300' : 'bg-light-200 text-light-600';
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'Beginner': return isDarkMode ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-green-50 text-green-800 border border-green-300 font-semibold';
      case 'Intermediate': return isDarkMode ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-amber-100 text-amber-950 border border-amber-300 font-bold';
      case 'Advanced': return isDarkMode ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-blue-50 text-blue-800 border border-blue-300 font-semibold';
      case 'Expert': return isDarkMode ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'bg-purple-50 text-purple-800 border border-purple-300 font-semibold';
      default: return isDarkMode ? 'bg-dark-700 text-dark-300' : 'bg-light-200 text-light-700';
    }
  };

  const handleDeleteSkill = async (skillOrId, optionalName) => {
    let skillId = '';
    let skillName = '';
    if (skillOrId && typeof skillOrId === 'object') {
      skillId = skillOrId.id || '';
      skillName = skillOrId.name || '';
    } else {
      skillId = skillOrId || '';
      skillName = optionalName || '';
    }

    // If skillName is missing or same as skillId, search in local skills array
    if (!skillName || skillName === skillId) {
      const found = skills.find(s => 
        (skillId && (String(s.id).toLowerCase() === String(skillId).toLowerCase() || String(s.name || '').toLowerCase() === String(skillId).toLowerCase()))
      );
      if (found) {
        skillName = found.name || skillName;
        skillId = found.id || skillId;
      }
    }

    const displayName = skillName || skillId || 'this skill';

    if (window.confirm(`Are you sure you want to delete "${displayName}"?`)) {
      // 1. Immediately update local UI state
      const targetId = String(skillId).toLowerCase();
      const targetName = String(skillName || '').toLowerCase();
      const updatedSkills = skills.filter(s => {
        const sId = String(s.id).toLowerCase();
        const sName = String(s.name || '').toLowerCase();
        const idMatches = targetId && (sId === targetId || sName === targetId);
        const nameMatches = targetName && (sName === targetName || sId === targetName);
        return !idMatches && !nameMatches;
      });
      setSkills(updatedSkills);

      // 2. Persist delete to backend and storage
      try {
        await api.deleteSkill(skillId, skillName);
      } catch (err) {
        console.error('Failed to delete skill on server:', err);
      }
    }
  };

  const stats = {
    total: skills.length,
    gap: skills.filter(s => s.gap > 0).length,
    surplus: skills.filter(s => s.gap < 0).length,
    trending: skills.filter(s => s.trend === 'up').length
  };

  return (
    <div className={`space-y-6 transition-colors duration-200 ${
      isDarkMode ? 'text-white' : 'text-light-950'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Skills</h1>
          <p className={`transition-colors duration-200 ${
            isDarkMode ? 'text-dark-300' : 'text-light-600'
          }`}>
            Manage and analyze your team's skill inventory
          </p>
        </div>
        
        <Link
          to="/skills/new"
          className="btn-primary flex items-center"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Skill
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className={`card transition-all duration-200 ${
          isDarkMode ? 'dark' : 'light'
        }`}>
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Target className="h-6 w-6 text-blue-400" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className={`text-sm transition-colors duration-200 ${
                isDarkMode ? 'text-dark-300' : 'text-light-600'
              }`}>
                Total Skills
              </div>
            </div>
          </div>
        </div>

        <div className={`card transition-all duration-200 ${
          isDarkMode ? 'dark' : 'light'
        }`}>
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <Target className="h-6 w-6 text-red-400" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats.gap}</div>
              <div className={`text-sm transition-colors duration-200 ${
                isDarkMode ? 'text-dark-300' : 'text-light-600'
              }`}>
                Skills Gap
              </div>
            </div>
          </div>
        </div>

        <div className={`card transition-all duration-200 ${
          isDarkMode ? 'dark' : 'light'
        }`}>
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <Target className="h-6 w-6 text-green-400" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats.surplus}</div>
              <div className={`text-sm transition-colors duration-200 ${
                isDarkMode ? 'text-dark-300' : 'text-light-600'
              }`}>
                Skills Surplus
              </div>
            </div>
          </div>
        </div>

        <div className={`card transition-all duration-200 ${
          isDarkMode ? 'dark' : 'light'
        }`}>
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <TrendingUp className="h-6 w-6 text-purple-400" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats.trending}</div>
              <div className={`text-sm transition-colors duration-200 ${
                isDarkMode ? 'text-dark-300' : 'text-light-600'
              }`}>
                Trending Up
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${
            isDarkMode ? 'text-dark-400' : 'text-light-500'
          }`} />
          <input
            type="text"
            placeholder="Search skills..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`input-field w-full pl-10 transition-all duration-200 ${
              isDarkMode ? 'dark' : 'light'
            }`}
          />
        </div>
        
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className={`input-field transition-all duration-200 ${
            isDarkMode ? 'dark' : 'light'
          }`}
        >
          <option value="all">All Categories</option>
          <option value="Frontend">Frontend</option>
          <option value="Backend">Backend</option>
          <option value="Design">Design</option>
          <option value="Database">Database</option>
          <option value="AI/ML">AI/ML</option>
          <option value="Infrastructure">Infrastructure</option>
        </select>
      </div>

      {/* Skills Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredSkills.map((skill) => (
          <div key={skill.id} className={`card transition-all duration-200 ${
            isDarkMode ? 'dark' : 'light'
          }`}>
            {/* Skill Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold mb-2">{skill.name}</h3>
                <p className={`text-sm transition-colors duration-200 ${
                  isDarkMode ? 'text-dark-300' : 'text-light-600'
                }`}>
                  {skill.description}
                </p>
              </div>
              <div className="flex space-x-2 ml-4">
                <Link
                  to={`/skills/${skill.id}/edit`}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-dark-700 rounded transition-colors"
                >
                  <Edit size={16} />
                </Link>
                <button
                  onClick={() => handleDeleteSkill(skill)}
                  className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500 rounded transition-colors"
                  title={`Delete ${skill.name}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            {/* Skill Tags */}
            <div className="flex items-center space-x-2 mb-4">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(skill.category)}`}>
                {skill.category}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLevelColor(skill.level)}`}>
                {skill.level}
              </span>
            </div>

            {/* Skill Stats */}
            <div className="space-y-3 mb-4">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-2">
                  <Users className={`h-4 w-4 ${
                    isDarkMode ? 'text-dark-400' : 'text-light-500'
                  }`} />
                  <span>People</span>
                </div>
                <span className="font-medium">{skill.peopleCount}</span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-2">
                  <Briefcase className={`h-4 w-4 ${
                    isDarkMode ? 'text-dark-400' : 'text-light-500'
                  }`} />
                  <span>Demand</span>
                </div>
                <span className="font-medium">{skill.projectDemand}</span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span>Gap</span>
                <span className={`font-medium ${getGapColor(skill.gap)}`}>
                  {skill.gap > 0 ? `+${skill.gap}` : skill.gap}
                </span>
              </div>
            </div>

            {/* Trend and Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <div className="flex items-center space-x-2">
                {getTrendIcon(skill.trend)}
                <span className="text-sm capitalize">{skill.trend}</span>
              </div>
              
              <Link
                to={`/skills/${skill.id}`}
                className="text-primary-500 hover:text-primary-400 text-sm font-medium"
              >
                View Details →
              </Link>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredSkills.length === 0 && (
        <div className={`text-center py-12 transition-colors duration-200 ${
          isDarkMode ? 'text-dark-300' : 'text-light-600'
        }`}>
          <p className="mb-6">No skills found matching your criteria.</p>
          <Link
            to="/skills/new"
            className="btn-primary"
          >
            Add Your First Skill
          </Link>
        </div>
      )}
    </div>
  );
};

export default Skills;
